import { useMemo } from "react";
import {
  Building2,
  Users,
  TrendingUp,
  DollarSign,
  ArrowUpRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
} from "recharts";

import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useDashboardHook } from "../../hooks/useDashboardHook";
import {
  Card,
  PageHeader,
  StatCard,
  CHART_COLORS,
} from "../adminComponents/adminComponents";
import "./dashboard.css";

const ICONS_BY_NAME = {
  "building-2": Building2,
  users: Users,
  "trending-up": TrendingUp,
  "dollar-sign": DollarSign,
};

export default function Dashboard() {
  const { user, token } = useAuth();
  const normalizedRole = String(user?.role ?? "").toLowerCase();
  const roleAllowed = [ROLES.ADMIN, ROLES.MANAGER].includes(normalizedRole);

  const {
    data: apiData,
    isLoading,
    error: queryError,
  } = useDashboardHook({
    token,
    role: user?.role,
    userId: user?.id,
    enabled: roleAllowed,
  });

  const toNumber = (value) => {
    const parsed = Number(String(value ?? "0").replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toPercent = (value) => {
    const raw = String(value ?? "");
    if (raw.includes("%")) return raw;
    return `${toNumber(raw)}%`;
  };

  const firstItemByLabel = (items = [], label) =>
    items.find((item) => item.label === label);

  const usingAdminShape = Boolean(apiData?.total_ventures);

  const venturesValue = usingAdminShape
    ? toNumber(apiData?.total_ventures?.value)
    : toNumber(apiData?.ventures_supported?.value);

  const venturesTrend = usingAdminShape
    ? toNumber(apiData?.total_ventures?.trend?.value)
    : toNumber(apiData?.ventures_supported?.trend?.value);

  const venturesDescription = usingAdminShape
    ? (apiData?.total_ventures?.trend?.label ?? "")
    : `${toNumber(apiData?.women_led_ventures?.portfolio_percent)}% women-led`;

  const jobsValue = usingAdminShape
    ? toNumber(apiData?.jobs_created?.value)
    : toNumber(apiData?.jobs_created?.value);

  const jobsTrend = usingAdminShape
    ? toNumber(apiData?.jobs_created?.trend?.value)
    : null;

  const jobsDescription = usingAdminShape
    ? (apiData?.jobs_created?.trend?.label ?? "")
    : "Full-time & part-time";

  const revenueValue = usingAdminShape
    ? (apiData?.portfolio_revenue?.value ?? "UGX 0")
    : ((apiData?.metrics?.financial ?? []).find(
        (item) => item.metric === "Portfolio Revenue",
      )?.value ?? "UGX 0");

  const revenueTrend = usingAdminShape
    ? toNumber(apiData?.portfolio_revenue?.trend?.value)
    : null;

  const revenueDescription = usingAdminShape
    ? (apiData?.portfolio_revenue?.trend?.label ?? "")
    : "Monthly aggregate";

  const capitalValue = usingAdminShape
    ? (apiData?.capital_deployed?.value ?? "UGX 0")
    : (apiData?.capital_deployed?.imagineher?.value ?? "UGX 0");

  const leverageText = usingAdminShape
    ? `${apiData?.capital_deployed?.trend?.value ?? "0%"} ${apiData?.capital_deployed?.trend?.label ?? "leveraged"}`
    : `${apiData?.capital_deployed?.external?.leverage ?? "0x"} leveraged`;

  const metricCards = useMemo(() => {
    const apiSummaryCards = Array.isArray(apiData?.summary_cards)
      ? apiData.summary_cards
      : [];

    if (apiSummaryCards.length > 0) {
      return apiSummaryCards.map((card, index) => ({
        id: `${card.title ?? "metric"}-${index}`,
        title: card.title ?? "Metric",
        value: card.value ?? "0",
        description: card.label ?? "",
        icon: ICONS_BY_NAME[card.icon] ?? Building2,
        trendValue:
          card?.trend?.value !== undefined && card?.trend?.value !== null
            ? toNumber(card.trend.value)
            : null,
      }));
    }

    return [
      {
        id: "total-ventures",
        title: "Total Ventures",
        value: venturesValue,
        description: venturesDescription,
        icon: Building2,
        trendValue: venturesTrend,
      },
      {
        id: "jobs-created",
        title: "Jobs Created",
        value: jobsValue.toLocaleString(),
        description: jobsDescription,
        icon: Users,
        trendValue: jobsTrend,
      },
      {
        id: "portfolio-revenue",
        title: "Portfolio Revenue",
        value: revenueValue,
        description: revenueDescription,
        icon: TrendingUp,
        trendValue: revenueTrend,
      },
      {
        id: "capital-deployed",
        title: "Capital Deployed",
        value: capitalValue,
        description: leverageText,
        icon: DollarSign,
        trendValue: null,
      },
    ];
  }, [
    apiData,
    venturesValue,
    venturesDescription,
    venturesTrend,
    jobsValue,
    jobsDescription,
    jobsTrend,
    revenueValue,
    revenueDescription,
    revenueTrend,
    capitalValue,
    leverageText,
  ]);

  const stageData = useMemo(() => {
    if (usingAdminShape) {
      return (apiData?.ventures_by?.stage ?? []).map((item, index) => ({
        name: item.stage,
        value: toNumber(item.count),
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }));
    }

    return [];
  }, [apiData, usingAdminShape]);

  const sectorData = useMemo(() => {
    return (apiData?.ventures_by?.sector ?? []).map((item) => ({
      name: item.sector,
      count: toNumber(item.count),
    }));
  }, [apiData]);

  const survival6 = usingAdminShape
    ? (firstItemByLabel(apiData?.survival_rates, "6 Months")?.rate ?? "0%")
    : (firstItemByLabel(
        apiData?.metrics?.survival_and_growth,
        "6-Month Survival",
      )?.value ?? "0%");

  const survival12 = usingAdminShape
    ? (firstItemByLabel(apiData?.survival_rates, "12 Months")?.rate ?? "0%")
    : (firstItemByLabel(
        apiData?.metrics?.survival_and_growth,
        "12-Month Survival",
      )?.value ?? "0%");

  const qolValue = usingAdminShape
    ? (apiData?.quality_of_life?.value ?? "0%")
    : (firstItemByLabel(
        apiData?.metrics?.survival_and_growth,
        "Avg. QoL Improvement",
      )?.value ?? "0%");

  const qolSubtitle = usingAdminShape
    ? (apiData?.quality_of_life?.subtitle ?? "")
    : "Average improvement in composite QoL score from baseline to midline";

  const recentActivity = usingAdminShape
    ? (apiData?.recent_activity ?? [])
    : [];

  const firstName = user?.name?.split(" ")[0] ?? "there";

  if (isLoading) {
    return (
      <div className="adminDashboard-root">
        <PageHeader
          title={`Welcome back, ${firstName}`}
          description="Loading dashboard data..."
        />
      </div>
    );
  }

  if (!roleAllowed) {
    return (
      <div className="adminDashboard-root">
        <PageHeader
          title="Dashboard"
          description="This dashboard is only available to admin and manager accounts."
        />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="adminDashboard-root">
        <PageHeader
          title="Dashboard"
          description="Missing auth token. Please sign in again."
        />
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="adminDashboard-root">
        <PageHeader
          title="Dashboard"
          description={queryError.message || "Unable to load dashboard data."}
        />
      </div>
    );
  }

  return (
    <div className="adminDashboard-root">
      <PageHeader
        title={`Welcome back, ${firstName}`}
        description="Here's an overview of your portfolio performance"
      />

      {/* Headline metrics */}
      <div className="admin-grid admin-grid-4 adminDashboard-stats">
        {metricCards.map((card) => (
          <StatCard
            key={card.id}
            title={card.title}
            value={card.value}
            description={card.description}
            icon={card.icon}
            trend={
              card.trendValue === null
                ? undefined
                : {
                    value: card.trendValue,
                    isPositive: card.trendValue >= 0,
                  }
            }
          />
        ))}
      </div>

      {/* Charts row */}
      <div className="admin-grid admin-grid-2 adminDashboard-charts">
        <Card title="Ventures by Stage">
          <div className="adminDashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Ventures by Sector">
          <div className="adminDashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sectorData} layout="vertical">
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal
                  vertical={false}
                />
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar
                  dataKey="count"
                  fill="hsl(327, 72%, 42%)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Highlights row */}
      <div className="admin-grid  adminDashboard-highlights">
        {/* <Card
          title={
            <span className="adminDashboard-cardTitle">
              <Target className="adminDashboard-cardIcon" /> Survival Rate
            </span>
          }
        >
          <div className="adminDashboard-survival">
            <div className="adminDashboard-survivalRow">
              <span>6 Months</span>
              <strong className="admin-text-success">
                {toPercent(survival6)}
              </strong>
            </div>
            <Progress value={toNumber(survival6)} tone="success" />
            <div className="adminDashboard-survivalRow">
              <span>12 Months</span>
              <strong className="admin-text-success">
                {toPercent(survival12)}
              </strong>
            </div>
            <Progress value={toNumber(survival12)} tone="success" />
          </div>
        </Card> */}

        <Card
          title={
            <span className="adminDashboard-cardTitle">
              <ArrowUpRight className="adminDashboard-cardIcon" /> Quality of
              Life
            </span>
          }
        >
          <div className="adminDashboard-qol">
            <div className="adminDashboard-qolValue">{toPercent(qolValue)}</div>
            <p className="admin-muted adminDashboard-qolDesc">{qolSubtitle}</p>
          </div>
        </Card>

        {/* <Card title="Recent Activity">
          <ul className="adminDashboard-activity">
            {recentActivity.slice(0, 4).map((activity, index) => (
              <li key={index} className="adminDashboard-activityItem">
                <div>
                  <p className="adminDashboard-activityName">
                    {activity.venture?.name}
                  </p>
                  <p className="admin-muted adminDashboard-activityMeta">
                    {activity.venture?.sector}
                  </p>
                </div>
                <Badge tone="primary">{activity.venture?.stage}</Badge>
              </li>
            ))}
          </ul>
        </Card> */}
      </div>
    </div>
  );
}
